import { ref, uploadBytes, getDownloadURL, deleteObject } from 'firebase/storage';
import { storage } from '../firebase';

function guessImageContentType(name = '') {
  const ext = String(name).split('.').pop().toLowerCase();
  const map = {
    jpg: 'image/jpeg',
    jpeg: 'image/jpeg',
    png: 'image/png',
    gif: 'image/gif',
    webp: 'image/webp',
    heic: 'image/heic',
    heif: 'image/heif',
    bmp: 'image/bmp',
    svg: 'image/svg+xml',
  };
  return map[ext] || 'image/jpeg';
}

function isImageFile(file) {
  if (!file) return false;
  if (file.type && String(file.type).startsWith('image/')) return true;
  return Boolean(guessImageContentType(file.name) && /\.(jpe?g|png|gif|webp|heic|heif|bmp|svg)$/i.test(file.name || ''));
}

/**
 * Normalize legacy single-image fields + new solvedImages array.
 * @returns {{ id: string, url: string, description: string, path: string }[]}
 */
export function normalizeSolvedImages(day = {}) {
  if (Array.isArray(day.solvedImages) && day.solvedImages.length > 0) {
    return day.solvedImages
      .filter((img) => img?.url)
      .map((img, i) => ({
        id: img.id || `img_${i}`,
        url: img.url,
        description: img.description || '',
        path: img.path || '',
      }));
  }
  if (day.solvedImageUrl) {
    return [
      {
        id: 'legacy',
        url: day.solvedImageUrl,
        description: day.solvedImageDescription || '',
        path: '',
      },
    ];
  }
  return [];
}

/** Join all descriptions for AI teaching prompts. */
export function combineSolvedImageDescriptions(dayOrImages) {
  const images = Array.isArray(dayOrImages)
    ? dayOrImages
    : normalizeSolvedImages(dayOrImages);
  return images
    .map((img, i) => {
      const desc = (img.description || '').trim();
      if (!desc) return '';
      return images.length > 1 ? `Image ${i + 1}:\n${desc}` : desc;
    })
    .filter(Boolean)
    .join('\n\n');
}

/**
 * Upload a pictorial worked-solution image for a lecture (admin only).
 * @returns {{ url: string, path: string }}
 */
export async function uploadLectureSolvedImage(file, moduleId, dayId = 'draft') {
  if (!isImageFile(file)) {
    throw new Error('Please upload an image file (PNG, JPG, WEBP).');
  }
  if (file.size > 8 * 1024 * 1024) {
    throw new Error('Image must be under 8 MB.');
  }

  const safeName = file.name.replace(/[^a-zA-Z0-9._-]/g, '_');
  const path = `lecture-images/${moduleId}/${dayId}_${Date.now()}_${Math.random().toString(36).slice(2, 8)}_${safeName}`;
  const storageRef = ref(storage, path);
  const contentType =
    file.type && String(file.type).startsWith('image/')
      ? file.type
      : guessImageContentType(file.name);
  await uploadBytes(storageRef, file, { contentType });
  const url = await getDownloadURL(storageRef);
  return { url, path };
}

/**
 * Upload a Social Studies map / visual-image for Images and map Readings.
 * @returns {{ url: string, path: string }}
 */
export async function uploadImageMapReadingImage(file, itemId = 'draft') {
  if (!file?.type?.startsWith('image/')) {
    throw new Error('Please upload an image file (PNG, JPG, WEBP).');
  }
  if (file.size > 8 * 1024 * 1024) {
    throw new Error('Image must be under 8 MB.');
  }

  const safeName = file.name.replace(/[^a-zA-Z0-9._-]/g, '_');
  const path = `image-map-readings/${itemId}/${Date.now()}_${safeName}`;
  const storageRef = ref(storage, path);
  await uploadBytes(storageRef, file);
  const url = await getDownloadURL(storageRef);
  return { url, path };
}

/** Delete an uploaded lecture image from Firebase Storage. */
export async function deleteLectureSolvedImage(pathOrUrl) {
  if (!pathOrUrl) return;
  try {
    let storageRef;
    if (String(pathOrUrl).startsWith('http')) {
      // Best-effort: cannot always map download URL → path; skip if no path stored
      return;
    }
    storageRef = ref(storage, pathOrUrl);
    await deleteObject(storageRef);
  } catch (err) {
    // Ignore missing files so UI can still remove the Firestore reference
    if (err?.code !== 'storage/object-not-found') {
      console.warn('Failed to delete lecture image:', err);
    }
  }
}
